import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { getBlogPosts } from '@/lib/api';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS } from '@/data/site';

const CATEGORY_LINKS = [
  { to: '/shop/exotic', label: 'Exotic Flower' },
  { to: '/shop/indica', label: 'Indica' },
  { to: '/shop/sativa', label: 'Sativa' },
  { to: '/shop/hybrid', label: 'Hybrid' },
  { to: '/shop/prerolls', label: 'Pre-Rolls' },
  { to: '/shop/cartridges', label: 'Vapes & Carts' },
  { to: '/shop/edibles', label: 'Edibles' },
  { to: '/shop/concentrates', label: 'Concentrates' },
  { to: '/deals', label: 'Deals' },
];

export default function Blog() {
  const { data: posts, isLoading } = useQuery({ queryKey: ['blog-posts'], queryFn: getBlogPosts, staleTime: 120000 });

  return (
    <>
      <SEO
        title={`Cannabis Blog — Guides & Education | Majestic Greens DMV, Washington DC`}
        description={`Cannabis guides, strain education and DC ordering tips from Majestic Greens DMV — Washington DC's premium cannabis retailer at ${BUSINESS.address}.`}
        path="/blog"
      />
      <div data-testid="blog-page" className="pb-28">
        <div className="border-b border-ink/10 bg-parchment py-20">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Guides & Education</p>
              <h1 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-6xl">The Majestic blog</h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-sage">
                Strain guides, cannabis education, and smart ordering tips — written for Washington, DC by the team at {BUSINESS.name}.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 pt-16">
          <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse bg-parchment" />)
              : (posts || []).map((post, i) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-${post.slug}`}
                    className="group flex flex-col bg-bone transition-colors hover:bg-parchment"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden bg-parchment">
                      {post.image ? (
                        <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-forest text-2xl font-black text-bone/30">MG</div>
                      )}
                      {i === 0 && (
                        <span className="absolute left-4 top-4 bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-ink">Latest</span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">{post.date}</span>
                      <h2 className="mt-2 text-lg font-extrabold leading-snug tracking-tight text-ink group-hover:text-forest">{post.title}</h2>
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-sage">{post.excerpt}</p>
                      <span className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-forest">
                        Read guide <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 border border-ink/10 bg-forest p-10">
              <h2 className="text-2xl font-extrabold tracking-tight text-bone">Reading is step one. Shopping is step two.</h2>
              <p className="mt-2 max-w-xl text-sm text-bone/70">
                Everything you just learned about is on the live menu — ${BUSINESS.minOrder} minimum, pickup or delivery, adults 21+.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {CATEGORY_LINKS.map((c) => (
                  <Link key={c.to} to={c.to} data-testid={`blog-shop-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="border border-bone/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-bone transition-colors hover:border-bone hover:bg-bone hover:text-ink">
                    {c.label}
                  </Link>
                ))}
              </div>
              <Link to="/shop" data-testid="blog-shop-all-cta" className="mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-[#c9a227]">
                Shop the Full Menu <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
